/**
 * The single React hook that owns the live session.
 *
 * Every mode — voice, conversation, subtitles — shares this. They differ only
 * in what they render, so putting the socket, the microphone, the recognizer
 * and the transcript in one place means there is exactly one implementation of
 * "start translating", and no mode can drift from the others.
 *
 * Design notes worth knowing:
 *  - The socket and the microphone are created lazily, on first `start()`. A
 *    page load must not ask for the microphone.
 *  - Both recognition paths are live at once. The browser recognizer produces
 *    instant partials; the server produces the authoritative transcript that
 *    goes into the transcript and into the context window. They are labelled
 *    differently in the UI so the user always knows which is which.
 *  - All teardown happens in one `useEffect` cleanup, because a leaked
 *    microphone stream is the single most common way this kind of app becomes
 *    a privacy problem.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { AppMode, AudioOutputMode, ConversationMode, SessionState, SpeakerId } from "@shared/constants";
import { AUDIO } from "@shared/constants";
import type { LanguageCode, LanguageDetection } from "@shared/types/language";
import type { FinalUtterance } from "@shared/types/protocol";
import type { ProviderHealth } from "@shared/types/provider";
import { EMPTY_TRANSCRIPT, type Transcript, type TranscriptEntry } from "@shared/types/transcript";
import { DreamTongueApi } from "./api";
import type { PublicConfig } from "@shared/types/api";
import { SessionClient, type SessionConfig } from "./session";
import { MicrophoneCapture, describeMicError } from "./microphone";
import { LocalRecognizer, LocalSpeaker, isBrowserAsrAvailable, isBrowserTtsAvailable } from "./browser-voice";
import {
  IDLE_PIPER_STATE,
  PiperSpeaker,
  R2_HOSTED_VOICES,
  isPiperSupported,
  resolvePiperVoiceId,
  type PiperGender,
  type PiperState,
  type ResolvedPiperVoice,
} from "./piper";
import { languageLabel, toLocale } from "@shared/languages";
import type { ToastTone } from "../components/ui";

export interface PartialLine {
  readonly speaker: SpeakerId;
  readonly source: string;
  readonly target: string;
  readonly updatedAt: number;
}

export interface UseTranslationOptions {
  readonly sourceLanguage: LanguageCode | "auto";
  readonly targetLanguage: LanguageCode;
  readonly mode: AppMode;
  readonly audioOutput: AudioOutputMode;
  readonly speakTranslations: boolean;
  readonly persistTranscript: boolean;
  readonly useBrowserAsr: boolean;
  readonly useBrowserTts: boolean;
  /** On-device Piper voices first — free and offline after one download. */
  readonly usePiperTts: boolean;
  readonly piperGender: PiperGender;
  /** Explicit Piper voice id override; null resolves from the language. */
  readonly piperVoiceId: string | null;
  readonly inputDeviceId: string | null;
  readonly partialTranslation: boolean;
  /** Person B's language in conversation mode. Ignored in every other mode. */
  readonly secondaryLanguage?: LanguageCode;
  /** `(title, tone, detail?)` — a toast factory, not a raw toast object. */
  readonly onToast: (title: string, tone: ToastTone, detail?: string) => void;
}
export interface TranslationSession {
  readonly state: SessionState;
  readonly stateDetail: string | undefined;
  readonly isLive: boolean;
  readonly isStarting: boolean;
  readonly isSupported: boolean;
  readonly unsupportedReason: string | null;
  readonly sessionId: string | null;
  readonly limits: Readonly<Record<string, number>>;
  readonly providers: readonly ProviderHealth[];

  readonly level: number;
  readonly partial: PartialLine | null;

  readonly transcript: Transcript;
  readonly detection: LanguageDetection | null;
  readonly quotaMessage: string | null;
  readonly usingBrowserAsr: boolean;
  readonly browserAsrActive: boolean;

  /** True while any local voice (Piper or browser) is playing. */
  readonly speaking: boolean;
  /** Piper engine state: download progress, voice, errors. */
  readonly piper: PiperState;
  /** The Piper voice currently resolved for the target language. */
  readonly piperVoice: ResolvedPiperVoice;
  /** Pre-download the target-language Piper voice so the first line is instant. */
  readonly ensurePiperVoice: () => Promise<void>;
  /** Pre-download the Piper voice for an explicit language (Voices page). */
  readonly ensurePiperVoiceFor: (language: LanguageCode) => Promise<void>;
  /** Voice ids already cached on this device. */
  readonly listCachedVoices: () => Promise<string[]>;
  /** Pre-download the voices for both sides of the conversation. */
  readonly ensurePiperPair: () => Promise<void>;
  /** Speak and report which engine spoke; rejects with the real reason. */
  readonly trySpeak: (text: string, language: LanguageCode) => Promise<"piper" | "browser" | "server">;
  /** Speak a sample line and toast which engine answered (or why none did). */
  readonly testVoice: (language: LanguageCode, sample: string) => Promise<void>;

  /** Which person the single microphone belongs to right now (spec §33). */
  readonly activeSpeaker: SpeakerId;
  readonly speakerLanguage: LanguageCode | "auto";

  start: () => Promise<void>;
  stop: () => Promise<void>;
  /** Reassign the microphone. Safe mid-session; flushes the previous speaker. */
  assignSpeaker: (speaker: SpeakerId) => void;
  reset: () => void;
  clearTranscript: () => void;
  translateText: (text: string) => Promise<{ translated: string; detection: LanguageDetection | null }>;
  detectText: (text: string) => Promise<LanguageDetection>;
  speak: (text: string, language: LanguageCode) => void;
  cancelSpeech: () => void;
  refreshConfig: () => Promise<PublicConfig | null>;
}

const conversationSpeakers = (mode: AppMode): readonly SpeakerId[] => (mode === "conversation" ? ["A", "B"] : ["A"]);

/**
 * The language a given person speaks.
 *
 * In conversation mode A speaks the source language and B speaks the secondary
 * one. In every other mode the source language is simply "whoever is talking",
 * so both map to it and only speaker A is ever used.
 */
function languageForSpeaker(options: UseTranslationOptions, speaker: SpeakerId): LanguageCode | "auto" {
  if (options.mode !== "conversation") return options.sourceLanguage;
  if (speaker === "A") return options.sourceLanguage;
  return options.secondaryLanguage ?? options.sourceLanguage;
}

export function useTranslationSession(options: UseTranslationOptions): TranslationSession {
  const [state, setState] = useState<SessionState>("idle");
  const [stateDetail, setStateDetail] = useState<string | undefined>(undefined);
  const [isStarting, setIsStarting] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [limits, setLimits] = useState<Readonly<Record<string, number>>>({});
  const [providers, setProviders] = useState<readonly ProviderHealth[]>([]);
  const [level, setLevel] = useState(0);
  const [partial, setPartial] = useState<PartialLine | null>(null);
  const [transcript, setTranscriptState] = useState<Transcript>(EMPTY_TRANSCRIPT);
  const [detection, setDetection] = useState<LanguageDetection | null>(null);
  const [quotaMessage, setQuotaMessage] = useState<string | null>(null);
  const [usingBrowserAsr, setUsingBrowserAsr] = useState(false);
  const [browserAsrActive, setBrowserAsrActive] = useState(false);
  const [config, setConfig] = useState<PublicConfig | null>(null);

  // Which person the single microphone belongs to. A ref as well as state: the
  // audio `onFrame` closure is created once per capture and must read the
  // current owner, not the one that happened to be active at start time.
  const [activeSpeaker, setActiveSpeaker] = useState<SpeakerId>("A");
  const activeSpeakerRef = useRef<SpeakerId>("A");
  activeSpeakerRef.current = activeSpeaker;

  // Latest-option ref so long-lived objects (the socket, the recognizer) never
  // capture stale settings and never need to be torn down and rebuilt when a
  // preference changes.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // `VITE_API_BASE` is empty for a single-origin deployment, where the Worker
  // serves the SPA and every request is same-origin. It is set to the Worker's
  // origin when the SPA is deployed separately (Pages + Worker).
  const api = useMemo(() => new DreamTongueApi(import.meta.env.VITE_API_BASE ?? ""), []);
  const speaker = useMemo(() => new LocalSpeaker(), []);
  const piper = useMemo(() => new PiperSpeaker(), []);
  const [speaking, setSpeaking] = useState(false);
  const [piperState, setPiperState] = useState<PiperState>(IDLE_PIPER_STATE);
  const [piperVoice, setPiperVoice] = useState<ResolvedPiperVoice>({ voiceId: null, fellBack: false, note: null, option: null });
  const serverAudioRef = useRef<HTMLAudioElement | null>(null);
  const socketRef = useRef<SessionClient | null>(null);
  const captureRef = useRef<MicrophoneCapture | null>(null);
  const recognizerRef = useRef<LocalRecognizer | null>(null);
  const levelTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const transcriptRef = useRef(transcript);
  transcriptRef.current = transcript;
  const entryIndexRef = useRef(0);

  // -- toasts ----------------------------------------------------------------

  const onToast = useCallback(
    (title: string, tone: ToastTone, detail?: string) => {
      optionsRef.current.onToast(title, tone, detail);
    },
    [],
  );

  // -- TTS playback ----------------------------------------------------------

  /**
   * Plays server-synthesized audio (an MP3 chunk from `tts.chunk`).
   *
   * `new Audio()` is used rather than `decodeAudioData` because the payload is
   * compressed; the browser's media stack handles the codec. The object URL is
   * revoked on `ended` so a long conversation does not leak blobs. The element
   * is tracked so `cancelSpeech` can actually stop it.
   */
  const playServerAudio = useCallback((audio: ArrayBuffer) => {
    try {
      serverAudioRef.current?.pause();
    } catch {
      // Ignore — stopping a previous clip must never break the next one.
    }
    const blob = new Blob([audio], { type: "audio/mpeg" });
    const url = URL.createObjectURL(blob);
    const element = new Audio(url);
    serverAudioRef.current = element;
    element.onended = () => {
      if (serverAudioRef.current === element) serverAudioRef.current = null;
      URL.revokeObjectURL(url);
    };
    element.onerror = () => {
      if (serverAudioRef.current === element) serverAudioRef.current = null;
      URL.revokeObjectURL(url);
    };
    element.play().catch(() => {
      // Autoplay was blocked. The translated text is already on screen, so this
      // is a degradation, not a failure.
      if (serverAudioRef.current === element) serverAudioRef.current = null;
      URL.revokeObjectURL(url);
    });
  }, []);

  // Voice state plumbing: exactly one place wires the two local engines to
  // React state, so the mic button, badges and stop controls always agree on
  // whether something is playing.
  useEffect(() => {
    speaker.onStateChange = (active) => setSpeaking(active);
    piper.onStateChange = (state) => {
      setPiperState(state);
      setSpeaking((current) => (state.status === "speaking" ? true : state.status === "ready" ? false : current));
    };
    return () => {
      speaker.onStateChange = null;
      piper.onStateChange = null;
    };
  }, [piper, speaker]);

  // -- server events ---------------------------------------------------------

  // Assigned by `speak` below. A ref (not a dependency) because `onFinal` is
  // created before `speak` and is captured by the long-lived socket.
  const speakRef = useRef<(text: string, language: LanguageCode) => void>(() => undefined);

  const onFinal = useCallback(
    (utterance: FinalUtterance) => {
      setPartial(null);
      // Finished utterance → read the translation aloud in the target
      // language. This is the "speak in English, hear Portuguese" path: the
      // text is already translated by the time it lands here, and `speak`
      // picks Piper first, then the browser voice, then the server.
      if (optionsRef.current.speakTranslations) {
        const text = utterance.translatedText || utterance.sourceText;
        if (text.trim() !== "") {
          // Defer a tick so the transcript paints before synthesis starts.
          const queued = text;
          const language = utterance.targetLanguage;
          setTimeout(() => speakRef.current(queued, language), 60);
        }
      }
      const entry: TranscriptEntry = {
        id: utterance.id,
        index: entryIndexRef.current,
        timestamp: new Date(utterance.startedAt).toLocaleTimeString([], { hour12: false }),
        startedAt: utterance.startedAt,
        endedAt: utterance.endedAt,
        startMs: utterance.startMs,
        endMs: utterance.endMs,
        speaker: utterance.speaker,
        sourceLanguage: utterance.sourceLanguage,
        sourceText: utterance.sourceText,
        targetLanguage: utterance.targetLanguage,
        translatedText: utterance.translatedText,
        detectionConfidence: utterance.detection?.confidence ?? null,
        status: "final",
        transcriptionProvider: "server",
        translationProvider: "server",
        transcriptionMs: utterance.transcriptionMs,
        translationMs: utterance.translationMs,
      };
      entryIndexRef.current += 1;
      setTranscriptState((current) => ({ ...current, entries: [...current.entries, entry], updatedAt: Date.now() }));
    },
    [],
  );

  const buildSocket = useCallback((): SessionClient => {
    const current = optionsRef.current;
    const sessionConfig: SessionConfig = {
      sourceLanguage: current.sourceLanguage,
      targetLanguage: current.targetLanguage,
      mode: (current.mode === "conversation" ? "two-way" : "solo") as ConversationMode,
      speakers: conversationSpeakers(current.mode),
      audioOutput: current.audioOutput,
      ttsEnabled: current.useBrowserTts || current.speakTranslations,
      persistTranscript: current.persistTranscript,
    };

    return new SessionClient(api, sessionConfig, {
      onState: (next, detail) => {
        setState(next);
        setStateDetail(detail);
      },
      onReady: (info) => {
        setSessionId(info.sessionId);
        setLimits(info.limits);
        setProviders(info.providers);
      },
      onPartial: ({ speaker: who, text }) => {
        setPartial((current) =>
          current && current.speaker === who ? { ...current, source: text, updatedAt: Date.now() } : { speaker: who, source: text, target: "", updatedAt: Date.now() },
        );
      },
      onFinal,
      onTranslationPartial: ({ utteranceId, speaker: who, text }) => {
        setPartial((current) => (current?.speaker === who || utteranceId.startsWith("partial-") ? { ...(current ?? { speaker: who, source: "", updatedAt: Date.now() }), target: text } : current));
      },
      onDetected: (result, override) => {
        setDetection(result);
        if (override) onToast("Language updated", "info", languageLabel(result.code ?? result.rawTag));
      },
      onTtsChunk: ({ audio }) => {
        // Server audio is the last resort, not an echo: when a local engine
        // (Piper or the browser voice) is handling speech, the finished
        // utterance is already being read aloud via `onFinal` and playing the
        // server clip too would double every line.
        const current = optionsRef.current;
        if (!current.speakTranslations) return;
        const localTts = (current.usePiperTts && isPiperSupported()) || (current.useBrowserTts && isBrowserTtsAvailable());
        if (!localTts) playServerAudio(audio);
      },
      onTtsStarted: () => setStateDetail("Speaking"),
      onTtsCompleted: () => undefined,
      onEntry: (entry) => {
        // The DO streams the entry too; this keeps the two paths from
        // double-adding when both are live.
        setTranscriptState((current) =>
          current.entries.some((existing) => existing.id === entry.id)
            ? current
            : { ...current, entries: [...current.entries, entry], updatedAt: Date.now() },
        );
      },
      onCleared: () => {
        entryIndexRef.current = 0;
        setTranscriptState((current) => ({ ...current, entries: [], updatedAt: Date.now() }));
        setPartial(null);
      },
      onPersisted: () => undefined,
      onQuota: (_remaining, _resetsAt, reason) => {
        setQuotaMessage(reason);
        onToast("AI usage limit reached", "warn", reason);
      },
      onError: (code, message, retryable) => {
        if (code === "cancelled") return;
        onToast(titleForError(code), retryable ? "warn" : "error", message);
      },
    });
  }, [api, onFinal, onToast, playServerAudio]);

  // -- start / stop ----------------------------------------------------------

  const teardown = useCallback(async (): Promise<void> => {
    if (levelTimerRef.current !== null) {
      clearInterval(levelTimerRef.current);
      levelTimerRef.current = null;
    }
    recognizerRef.current?.stop();
    recognizerRef.current = null;
    setBrowserAsrActive(false);
    speaker.cancel();
    piper.cancel();
    try {
      serverAudioRef.current?.pause();
    } catch {
      // Ignore.
    }
    serverAudioRef.current = null;

    const socket = socketRef.current;
    const capture = captureRef.current;
    socketRef.current = null;
    captureRef.current = null;

    await capture?.stop().catch(() => undefined);
    await socket?.disconnect().catch(() => undefined);

    setLevel(0);
    setPartial(null);
    setSessionId(null);
  }, [piper, speaker]);

  const start = useCallback(async () => {
    if (state === "listening" || isStarting) return;
    setIsStarting(true);
    setQuotaMessage(null);

    const current = optionsRef.current;
    const browserAsr = current.useBrowserAsr && isBrowserAsrAvailable();
    const who = activeSpeakerRef.current;
    const ownLanguage = languageForSpeaker(current, who);

    try {
      // 1. Socket first. If the session cannot be created there is no point
      //    holding the microphone open.
      const socket = buildSocket();
      socketRef.current = socket;
      await socket.connect();

      const capture = new MicrophoneCapture({
        onFrame: (frame) => socket.sendAudio(activeSpeakerRef.current, floatToInt16(frame)),
        onError: () => undefined,
        onEnded: () => setState((current) => (current === "listening" ? "connected" : current)),
      });
      captureRef.current = capture;

      await capture.start(current.inputDeviceId ? { deviceId: current.inputDeviceId } : {});
      socket.startAudio(who, 16_000);
      // Declare every speaker's own language up front, not just the active one.
      // The server routes each turn into the *other* speaker's language, so an
      // undeclared B would have A's turns translated into the wrong language.
      for (const id of conversationSpeakers(current.mode)) {
        socket.assignSpeaker(id, languageForSpeaker(current, id));
      }
      socket.updateSettings({
        audioOutput: current.audioOutput,
        ttsEnabled: current.useBrowserTts || current.speakTranslations,
        persistTranscript: current.persistTranscript,
      });

      // 2. Browser recognizer, when available. It gives instant partials while
      //    the server pipeline settles.
      if (browserAsr) {
        const recognizer = new LocalRecognizer(
          (event) => {
            // Interim results update the stream in place. Final results are
            // deliberately silent: Chrome emits one every few seconds of
            // speech, so a toast per final buried the interface under
            // "Browser transcript" popups and crowded out real messages. The
            // authoritative text is the server's, and it arrives in the
            // transcript — `BrowserAsrNotice` already says which path is live.
            if (event.final) return;
            setPartial((currentPartial) => ({
              speaker: activeSpeakerRef.current,
              source: event.text,
              target: currentPartial?.target ?? "",
              updatedAt: Date.now(),
            }));
          },
          toLocale(ownLanguage === "auto" ? "en" : ownLanguage),
          (running) => setBrowserAsrActive(running),
        );
        recognizerRef.current = recognizer;
        // `isRunning` turns true asynchronously in `onstart`; the running
        // callback above is what reports it. Reading it here always saw
        // false, which is why the "on-device transcription is on" strip
        // never appeared.
        setUsingBrowserAsr(recognizer.start());
      } else {
        setUsingBrowserAsr(false);
      }

      // 3. Level meter, sampled rather than pushed, so it costs one timer.
      levelTimerRef.current = setInterval(() => setLevel(captureRef.current?.level ?? 0), 80);

      setState("listening");
    } catch (error) {
      await teardown();
      const message = describeMicError(error);
      setState("error");
      setStateDetail(message);
      onToast("Cannot start", "error", message);
    } finally {
      setIsStarting(false);
    }
  }, [buildSocket, isStarting, onToast, state, teardown]);

  const stop = useCallback(async () => {
    // Flush first, then wait: the trailing segment is still being
    // transcribed/translated server-side, and disconnecting immediately closes
    // the socket before its final arrives — the "last sentence is always
    // lost" bug. `session.end` also flushes, but nothing can be received on a
    // closed socket, so the pause has to happen here.
    const socket = socketRef.current;
    if (socket) {
      try {
        socket.flushAudio(activeSpeakerRef.current);
      } catch {
        // Flushing is best-effort; teardown still runs.
      }
      await new Promise((resolve) => setTimeout(resolve, 900));
    }
    await teardown();
    setState("connected");
    setStateDetail("Stopped");
  }, [teardown]);

  /**
   * Hand the single microphone to the other person, mid-conversation.
   *
   * The previous speaker is flushed rather than stopped, so a half-finished
   * sentence still gets translated instead of being silently discarded, and the
   * new one is opened so the next frame lands in the right segment.
   */
  const assignSpeaker = useCallback(
    (next: SpeakerId) => {
      const previous = activeSpeakerRef.current;
      if (next === previous) return;
      setActiveSpeaker(next);

      const socket = socketRef.current;
      if (!socket) return;

      const options = optionsRef.current;
      if (options.mode === "conversation") {
        socket.flushAudio(previous);
        socket.startAudio(next, 16_000);
        socket.assignSpeaker(next, languageForSpeaker(options, next));
      }
      setPartial(null);
      setDetection(null);
    },
    [],
  );

  // -- teardown on unmount ----------------------------------------------------

  useEffect(() => {
    const activeSocket = socketRef;
    const activeCapture = captureRef;
    const activeRecognizer = recognizerRef;
    const activeLevelTimer = levelTimerRef;
    return () => {
      if (activeLevelTimer.current !== null) clearInterval(activeLevelTimer.current);
      activeRecognizer.current?.stop();
      void activeCapture.current?.stop().catch(() => undefined);
      void activeSocket.current?.disconnect().catch(() => undefined);
    };
  }, []);

  // -- imperative operations --------------------------------------------------

  const reset = useCallback(() => {
    socketRef.current?.resetConversation();
    entryIndexRef.current = 0;
    setTranscriptState((current) => ({ ...current, entries: [], updatedAt: Date.now() }));
    setPartial(null);
    setDetection(null);
  }, []);

  const clearTranscript = useCallback(() => {
    setTranscriptState((current) => ({ ...current, entries: [], transcriptId: null, updatedAt: Date.now() }));
    entryIndexRef.current = 0;
  }, []);

  const translateText = useCallback(
    async (text: string): Promise<{ translated: string; detection: LanguageDetection | null }> => {
      const current = optionsRef.current;
      const socket = socketRef.current;
      if (socket?.isConnected) {
        const [result] = await socket.translateNow(text, current.sourceLanguage, current.targetLanguage);
        return { translated: result?.translatedText ?? text, detection: null };
      }

      const response = await api.translate({
        text,
        sourceLanguage: current.sourceLanguage,
        targetLanguage: current.targetLanguage,
      });
      if (!response.ok) throw new Error(response.error.message);
      const first = response.value.results[0];
      return {
        translated: first?.translatedText ?? text,
        detection: first?.detectedSourceLanguage ? { code: first.detectedSourceLanguage, rawTag: first.detectedSourceLanguage, label: first.detectedSourceLanguage, confidence: 0, provider: response.value.results[0]?.provider ?? "server", resolved: true } : null,
      };
    },
    [api],
  );

  /**
   * Detects a language over the session socket when live, and over REST
   * otherwise. Detection is cheap enough that this needs no debouncing of its
   * own — the caller decides when to ask.
   */
  const detectText = useCallback(
    async (text: string): Promise<LanguageDetection> => {
      const socket = socketRef.current;
      if (socket?.isConnected) return socket.detectNow(text);

      const response = await api.detect({ text });
      if (!response.ok) throw new Error(response.error.message);
      return response.value;
    },
    [api],
  );

  /**
   * Read `text` aloud in `language`. Resolves with the engine that spoke.
   *
   * Order is deliberate: Piper (on-device neural voice, male/female per the
   * picker, offline after one download) → the browser's own speech synthesis
   * (free, instant) → server synthesis (costs inference, needs the network).
   * The first path that accepts the text wins; a Piper failure falls through
   * instead of leaving the user in silence. Throws only when every path
   * failed, so the test button can report the real reason.
   */
  const trySpeak = useCallback(
    async (text: string, language: LanguageCode): Promise<"piper" | "browser" | "server"> => {
      if (text.trim() === "") throw new Error("Nothing to speak.");
      const current = optionsRef.current;
      let piperError: unknown = null;
      if (current.usePiperTts && isPiperSupported()) {
        try {
          const available = await piper.listVoices().catch(() => null);
          const resolved = resolvePiperVoiceId({
            language,
            gender: current.piperGender,
            customVoiceId: current.piperVoiceId,
            available,
          });
          setPiperVoice(resolved);
          if (resolved.voiceId) {
            const played = await piper.speak(text, resolved.voiceId);
            if (played) return "piper";
          }
        } catch (error) {
          piperError = error;
        }
      }
      if (current.useBrowserTts && isBrowserTtsAvailable()) {
        const spoken = speaker.speak(text, toLocale(language));
        if (spoken) return "browser";
      }
      // Server synthesis is the slowest path; use it only when no local
      // engine can speak this language.
      try {
        const result = await api.synthesize(text, language);
        if (result.ok) {
          playServerAudio(result.value);
          return "server";
        }
        throw new Error(result.error.message);
      } catch (error) {
        // The text is on screen; audio failing is a degradation, not data
        // loss — but the test button needs the truth, so it throws.
        if (piperError instanceof Error) throw piperError;
        throw error;
      }
    },
    [api, piper, playServerAudio, speaker],
  );

  const speak = useCallback(
    (text: string, language: LanguageCode) => {
      void trySpeak(text, language).catch(() => undefined);
    },
    [trySpeak],
  );
  speakRef.current = speak;

  const testVoice = useCallback(
    async (language: LanguageCode, sample: string): Promise<void> => {
      try {
        const path = await trySpeak(sample, language);
        if (path === "piper") {
          onToast("Voice test passed", "success", "Heard via the on-device Piper voice — translations will sound like this.");
        } else {
          onToast("Spoken with fallback", "warn", `Piper did not speak (${path} voice did instead). See the voice row for the reason.`);
        }
      } catch (error) {
        onToast("Voice test failed", "error", error instanceof Error ? error.message : undefined);
      }
    },
    [onToast, trySpeak],
  );

  /** Pre-download the Piper voice for an explicit language on user request. */
  const ensurePiperVoiceFor = useCallback(
    async (language: LanguageCode): Promise<void> => {
      if (!isPiperSupported()) {
        onToast("Piper voices need this browser", "warn", "On-device neural voices require WebAssembly and Web Workers. The browser voice will be used instead.");
        return;
      }
      const current = optionsRef.current;
      try {
        const available = await piper.listVoices();
        const resolved = resolvePiperVoiceId({
          language,
          gender: current.piperGender,
          customVoiceId: language === current.targetLanguage ? current.piperVoiceId : null,
          available,
        });
        if (language === current.targetLanguage) setPiperVoice(resolved);
        if (!resolved.voiceId) {
          onToast("No Piper voice for this language", "warn", resolved.note ?? undefined);
          return;
        }
        await piper.ensureVoice(resolved.voiceId, { baseUrl: api.voiceBaseUrl() });
        onToast("Voice ready", "success", `Piper voice ${resolved.voiceId} is cached on this device and works offline.`);
      } catch (error) {
        onToast("Voice download failed", "error", error instanceof Error ? error.message : undefined);
      }
    },
    [api, onToast, piper],
  );

  /** Pre-download the current target-language Piper voice on user request. */
  const ensurePiperVoice = useCallback(() => ensurePiperVoiceFor(optionsRef.current.targetLanguage), [ensurePiperVoiceFor]);

  /** Voice ids already cached on this device. */
  const listCachedVoices = useCallback(() => piper.cachedVoices(), [piper]);

  /**
   * Download the voices for both sides of the conversation (source and
   * target in the preferred gender) so switching speakers never waits on
   * the network. Each model is ~60 MB once, then cached on-device.
   */
  const ensurePiperPair = useCallback(async (): Promise<void> => {
    if (!isPiperSupported()) {
      onToast("Piper voices need this browser", "warn", "On-device neural voices require WebAssembly and Web Workers.");
      return;
    }
    const current = optionsRef.current;
    const source = current.sourceLanguage === "auto" ? "en" : current.sourceLanguage;
    const wanted = [...new Set([current.targetLanguage, source as LanguageCode])];
    try {
      const available = await piper.listVoices();
      const ids: string[] = [];
      for (const language of wanted) {
        const resolved = resolvePiperVoiceId({ language, gender: current.piperGender, customVoiceId: null, available });
        if (resolved.voiceId) ids.push(resolved.voiceId);
      }
      const unique = [...new Set(ids)];
      if (unique.length === 0) {
        onToast("No Piper voices for these languages", "warn");
        return;
      }
      for (const voiceId of unique) {
        await piper.ensureVoice(voiceId, { baseUrl: api.voiceBaseUrl() });
      }
      onToast("Voices ready", "success", `${unique.join(", ")} cached on this device — speech is instant and offline from here.`);
    } catch (error) {
      onToast("Voice download failed", "error", error instanceof Error ? error.message : undefined);
    }
  }, [api, onToast, piper]);

  const cancelSpeech = useCallback(() => {
    piper.cancel();
    speaker.cancel();
    try {
      serverAudioRef.current?.pause();
    } catch {
      // Ignore.
    }
    serverAudioRef.current = null;
    setSpeaking(false);
  }, [piper, speaker]);

  const refreshConfig = useCallback(async (): Promise<PublicConfig | null> => {
    const result = await api.config();
    if (!result.ok) {
      onToast("Cannot reach the server", "error", result.error.message);
      return null;
    }
    setConfig(result.value);
    return result.value;
  }, [api, onToast]);

  // The settings are the session's source of truth, so a change here is pushed
  // to a live session rather than only taking effect on the next one.
  useEffect(() => {
    socketRef.current?.updateSettings({
      audioOutput: options.audioOutput,
      ttsEnabled: options.useBrowserTts || options.speakTranslations,
      persistTranscript: options.persistTranscript,
    });
  }, [options.audioOutput, options.persistTranscript, options.speakTranslations, options.useBrowserTts]);

  useEffect(() => {
    socketRef.current?.changeLanguages(options.sourceLanguage, options.targetLanguage);
  }, [options.sourceLanguage, options.targetLanguage]);

  // The browser recognizer outlives language changes (it is created once per
  // session), so a mid-session language switch has to be pushed into it —
  // otherwise it keeps listening for the old language until the next start.
  useEffect(() => {
    const recognizer = recognizerRef.current;
    if (!recognizer) return;
    const own = languageForSpeaker(options, activeSpeakerRef.current);
    recognizer.setLanguage(toLocale(own === "auto" ? "en" : own));
  }, [options, options.sourceLanguage, options.secondaryLanguage, options.mode]);

  // Pre-warm the target-language Piper voice shortly after it becomes
  // relevant: if the model is already cached, WASM + the session initialize
  // now so the first translated line speaks with only inference latency. A
  // voice that was never downloaded is left alone — a ~60 MB download is the
  // user's explicit call via the pre-download buttons, never a surprise.
  // (Portuguese is the exception below: it seeds itself.)
  useEffect(() => {
    if (!options.speakTranslations || !options.usePiperTts || !isPiperSupported()) return;
    const resolved = resolvePiperVoiceId({
      language: options.targetLanguage,
      gender: options.piperGender,
      customVoiceId: options.piperVoiceId,
      available: null,
    });
    if (!resolved.voiceId) return;
    const voiceId = resolved.voiceId;
    const timer = setTimeout(() => {
      setPiperVoice(resolved);
      void piper.prewarm(voiceId).catch(() => undefined);
    }, 1_500);
    return () => clearTimeout(timer);
  }, [options.speakTranslations, options.usePiperTts, options.targetLanguage, options.piperGender, options.piperVoiceId, piper]);

  // Default voices seed themselves from the deployment's own hosting into
  // the on-device cache the moment they become the target — once per voice
  // per page load, in the background, with progress in the voice row.
  // Afterwards speaking needs no network at all. Only R2-hosted voices
  // auto-seed; anything else stays an explicit tap (no surprise downloads).
  const seededDefaultsRef = useRef<ReadonlySet<string>>(new Set());
  useEffect(() => {
    if (!options.usePiperTts || !isPiperSupported()) return;
    const resolved = resolvePiperVoiceId({
      language: options.targetLanguage,
      gender: options.piperGender,
      customVoiceId: options.piperVoiceId,
      available: null,
    });
    const voiceId = resolved.voiceId;
    if (!voiceId || !R2_HOSTED_VOICES.has(voiceId) || seededDefaultsRef.current.has(voiceId)) return;
    seededDefaultsRef.current = new Set([...seededDefaultsRef.current, voiceId]);
    const timer = setTimeout(() => {
      setPiperVoice((current) => (current.voiceId ? current : resolved));
      void piper.seedVoice(voiceId, api.voiceBaseUrl()).catch(() => undefined);
    }, 2_000);
    return () => clearTimeout(timer);
  }, [api, options.piperGender, options.piperVoiceId, options.targetLanguage, options.usePiperTts, piper]);

  // Load configuration once on mount so the UI can be honest about capabilities.
  useEffect(() => {
    void refreshConfig();
  }, [refreshConfig]);

  // -- derived ---------------------------------------------------------------

  const unsupportedReason = useMemo(() => {
    if (config === null) return null;
    if (config.mockAi) return "Mock AI is enabled — transcripts are simulated, not real.";
    if (!config.capabilities.translation.available) {
      return "No translation provider is configured. Set TRANSLATION_PROVIDER or add a Workers AI binding.";
    }
    return null;
  }, [config]);

  const isSupported = MicrophoneCapture.supported;

  return {
    state,
    stateDetail,
    isLive: state === "listening",
    isStarting,
    isSupported,
    unsupportedReason,
    sessionId,
    limits,
    providers,
    level,
    partial,
    transcript,
    detection,
    quotaMessage,
    usingBrowserAsr,
    browserAsrActive,
    speaking,
    piper: piperState,
    piperVoice,
    ensurePiperVoice,
    ensurePiperVoiceFor,
    listCachedVoices,
    ensurePiperPair,
    trySpeak,
    testVoice,
    activeSpeaker,
    speakerLanguage: languageForSpeaker(options, activeSpeaker),
    start,
    stop,
    assignSpeaker,
    reset,
    clearTranscript,
    translateText,
    detectText,
    speak,
    cancelSpeech,
    refreshConfig,
  };
}

// ---------------------------------------------------------------------------
//  Helpers
// ---------------------------------------------------------------------------

/** Float [-1, 1] to little-endian Int16. */
function floatToInt16(frame: Float32Array): Int16Array {
  const out = new Int16Array(frame.length);
  for (let i = 0; i < frame.length; i += 1) {
    const sample = Math.max(-1, Math.min(1, frame[i] ?? 0));
    out[i] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
  }
  return out;
}

function titleForError(code: string): string {
  switch (code) {
    case "bad_request":
      return "Invalid request";
    case "rate_limited":
      return "Too many requests";
    case "quota_exceeded":
      return "AI usage limit reached";
    case "session_expired":
      return "Session expired";
    case "provider_unavailable":
      return "Provider unavailable";
    case "model_unavailable":
      return "Model unavailable";
    case "language_unsupported":
      return "Language not supported";
    case "network":
      return "Connection lost";
    case "timeout":
      return "The request timed out";
    default:
      return "Something went wrong";
  }
}

export { AUDIO };
